import { useState, useMemo, useRef, useEffect } from "react"
import { useTareas } from "../context/TareasContext"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"
import { FaSearch, FaFilter, FaTimes, FaPlus, FaTrash, FaChevronDown } from "react-icons/fa"

const CATEGORY_ORDER = ["No iniciado", "En progreso", "Completado", "Cancelado"]
const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"]

const PRESET_COLORS = [
  "#6B7280","#3B82F6","#10B981","#F59E0B",
  "#EF4444","#8B5CF6","#EC4899","#06B6D4",
  "#F97316","#84CC16",
]

const EMPTY_ESTADO = { name: "", color: "#6B7280", category: "No iniciado", order: 0 }

const EstadoForm = ({ initial = EMPTY_ESTADO, onSave, onCancel, isGlobal }) => {
  const [form, setForm] = useState(initial)

  return (
    <div className="flex flex-col gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl mt-2">
      <input
        type="text"
        placeholder="Nombre del estado..."
        value={form.name}
        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
        className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
        autoFocus
      />
      <div className="flex items-center gap-2 flex-wrap">
        {PRESET_COLORS.map(c => (
          <button
            key={c}
            onClick={() => setForm(p => ({ ...p, color: c }))}
            className="w-5 h-5 rounded-full border-2 transition-all flex-shrink-0"
            style={{
              backgroundColor: c,
              borderColor: form.color === c ? "#1F2937" : "transparent",
              transform: form.color === c ? "scale(1.2)" : "scale(1)",
            }}
          />
        ))}
        <input
          type="color"
          value={form.color}
          onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
          className="w-5 h-5 rounded cursor-pointer border-0 p-0"
          title="Color personalizado"
        />
      </div>
      <select
        value={form.category}
        onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
      >
        {CATEGORY_ORDER.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
      <input
        type="number"
        placeholder="Orden (0, 1, 2...)"
        value={form.order}
        onChange={e => setForm(p => ({ ...p, order: Number(e.target.value) }))}
        className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
      />
      {isGlobal && (
        <p className="text-[10px] text-amber-600 bg-amber-50 rounded px-2 py-1">
          Sin proyecto seleccionado — se creará como estado global
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => form.name.trim() && onSave(form)}
          disabled={!form.name.trim()}
          className="flex-1 text-xs py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-40 transition-colors"
        >
          Guardar
        </button>
        <button
          onClick={onCancel}
          className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

const FilterBar = () => {
  const {
    filters, applyFilters, workers, estadosTareas,
    createEstadoTarea, deleteEstadoTarea, selectedProject,
  } = useTareas()

  const estadosByCategory = useMemo(() => {
    const grouped = {}
    CATEGORY_ORDER.forEach(cat => { grouped[cat] = [] })
    estadosTareas.forEach(e => {
      const cat = e.category || "No iniciado"
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(e)
    })
    return Object.entries(grouped).filter(([, list]) => list.length > 0)
  }, [estadosTareas])

  const [localSearch, setLocalSearch] = useState(filters.search || "")
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showEstadoForm, setShowEstadoForm] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const advancedRef = useRef(null)

  useEffect(() => {
    if (!showAdvanced) return
    const handler = (e) => {
      if (advancedRef.current && !advancedRef.current.contains(e.target)) {
        setShowAdvanced(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [showAdvanced])

  const projectTagsForFilter = useMemo(() => {
    const tags = selectedProject?.tags || []
    return tags.filter(t => t && !t.archived)
  }, [selectedProject?.tags])

  const hasActiveFilters =
    filters.search || filters.worker ||
    filters.status?.length || filters.priority?.length ||
    (filters.categories?.length ?? 0) > 0 ||
    (filters.projectTagIds?.length ?? 0) > 0 ||
    filters.dateFrom || filters.dateTo

  const handleSearchBlur = () => applyFilters({ ...filters, search: localSearch })

  const toggleStatus = (statusId) => {
    const current = filters.status || []
    const next = current.includes(statusId) ? current.filter(x => x !== statusId) : [...current, statusId]
    applyFilters({ ...filters, status: next })
  }

  const togglePriority = (p) => {
    const current = filters.priority || []
    const next = current.includes(p) ? current.filter(x => x !== p) : [...current, p]
    applyFilters({ ...filters, priority: next })
  }

  const clearFilters = () => {
    setLocalSearch("")
    applyFilters({
      search: "",
      status: [],
      worker: "",
      priority: [],
      categories: [],
      projectTagIds: [],
      dateFrom: "",
      dateTo: "",
    })
  }

  const toggleCategory = (cat) => {
    const current = filters.categories || []
    const next = current.includes(cat) ? current.filter(c => c !== cat) : [...current, cat]
    applyFilters({ ...filters, categories: next })
  }

  const toggleProjectTag = (tagId) => {
    const current = filters.projectTagIds || []
    const next = current.includes(tagId) ? current.filter(x => x !== tagId) : [...current, tagId]
    applyFilters({ ...filters, projectTagIds: next })
  }

  const handleCreateEstado = async (form) => {
    await createEstadoTarea(form)
    setShowEstadoForm(false)
  }

  const handleDeleteEstado = async (e, estadoId) => {
    e.stopPropagation()
    const ok = window.confirm("¿Eliminar este estado? Las tareas con este estado quedarán sin estado asignado.")
    if (!ok) return
    setDeletingId(estadoId)
    await deleteEstadoTarea(estadoId)
    setDeletingId(null)
  }

  const activeCategoryCount = filters.categories?.length ?? 0
  const activeFilterCount =
    (filters.status?.length ?? 0) +
    (filters.priority?.length ?? 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.projectTagIds?.length ?? 0)

  return (
    <div className="flex items-center gap-2 px-4 py-1.5 bg-white border-b border-gray-200 flex-wrap min-h-0">

      {/* Búsqueda */}
      <div className="relative">
        <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
        <input
          type="text"
          placeholder="Buscar tarea..."
          value={localSearch}
          onChange={e => setLocalSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && applyFilters({ ...filters, search: localSearch })}
          onBlur={handleSearchBlur}
          className="pl-7 pr-2.5 py-1 text-xs border border-gray-200 rounded-lg w-44 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:w-56 transition-all"
        />
      </div>

      {/* Trabajador */}
      <select
        value={filters.worker || ""}
        onChange={e => applyFilters({ ...filters, worker: e.target.value })}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-300 max-w-[140px]"
      >
        <option value="">Todos</option>
        {workers.map(w => (
          <option key={w.id} value={w.id}>{formatWorkerDisplayName(w.name || w.email)}</option>
        ))}
      </select>

      {/* Separador */}
      <div className="h-4 w-px bg-gray-200 flex-shrink-0" />

      {/* Chips de categoría inline */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-[10px] text-gray-400 font-medium mr-0.5">Cat:</span>
        {CATEGORY_ORDER.map(cat => {
          const active = filters.categories?.includes(cat)
          return (
            <button
              key={cat}
              onClick={() => toggleCategory(cat)}
              className={`text-[10px] px-2 py-0.5 rounded-md border transition-colors whitespace-nowrap ${
                active
                  ? "border-orange-400 bg-orange-50 text-orange-700 font-medium"
                  : "border-gray-200 text-gray-500 hover:border-gray-300"
              }`}
            >
              {cat}
            </button>
          )
        })}
        {activeCategoryCount > 0 && (
          <button
            onClick={() => applyFilters({ ...filters, categories: [] })}
            className="text-[10px] text-gray-400 hover:text-orange-600 px-1"
            title="Quitar filtro de categoría"
          >
            <FaTimes className="text-[8px]" />
          </button>
        )}
      </div>

      {/* Tags del proyecto (OR: tarea con al menos uno de los seleccionados) */}
      {projectTagsForFilter.length > 0 && (
        <>
          <div className="h-4 w-px bg-gray-200 flex-shrink-0" />
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap max-w-[min(420px,45vw)]">
            <span className="text-[10px] text-gray-400 font-medium mr-0.5">Tags:</span>
            {projectTagsForFilter.map(tag => {
              const active = filters.projectTagIds?.includes(tag.id)
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleProjectTag(tag.id)}
                  title={tag.name}
                  className={`text-[10px] px-2 py-0.5 rounded-md border transition-colors max-w-[120px] truncate border-l-[3px] ${
                    active
                      ? "border-orange-400 bg-orange-50 text-orange-800 font-medium"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  style={{ borderLeftColor: tag.color || "#6B7280" }}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ backgroundColor: tag.color || "#6B7280" }} />
                  {tag.name}
                </button>
              )
            })}
            {(filters.projectTagIds?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={() => applyFilters({ ...filters, projectTagIds: [] })}
                className="text-[10px] text-gray-400 hover:text-orange-600 px-1"
                title="Quitar filtro de tags"
              >
                <FaTimes className="text-[8px]" />
              </button>
            )}
          </div>
        </>
      )}

      {/* Separador */}
      <div className="h-4 w-px bg-gray-200 flex-shrink-0" />

      {/* Filtros avanzados (dropdown) */}
      <div ref={advancedRef} className="relative flex-shrink-0">
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-colors ${
            showAdvanced || activeFilterCount > 0
              ? "border-orange-400 bg-orange-50 text-orange-600"
              : "border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}
        >
          <FaFilter className="text-[10px]" />
          Filtros
          {activeFilterCount > 0 && (
            <span className="bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold leading-none">
              {activeFilterCount}
            </span>
          )}
          <FaChevronDown className={`text-[9px] transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
        </button>

        {showAdvanced && (
          <div className="absolute left-0 top-full mt-1.5 z-30 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-[580px] max-w-[90vw]">
            <div className="flex gap-6 flex-wrap">

              {/* Estados */}
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Estados</span>
                  <button
                    onClick={() => setShowEstadoForm(v => !v)}
                    className="flex items-center gap-1 text-[10px] text-orange-500 hover:text-orange-700 font-medium"
                  >
                    <FaPlus className="text-[9px]" /> Nuevo
                  </button>
                </div>

                {showEstadoForm && (
                  <EstadoForm
                    onSave={handleCreateEstado}
                    onCancel={() => setShowEstadoForm(false)}
                    isGlobal={!selectedProject?.id}
                  />
                )}

                <div className="flex flex-col gap-2.5 mt-1">
                  {estadosByCategory.map(([category, estados]) => (
                    <div key={category}>
                      <span className="text-[10px] text-gray-400 block mb-1">{category}</span>
                      <div className="flex flex-wrap gap-1">
                        {estados.map(e => {
                          const isProjectEstado = !!e.proyecto
                          return (
                            <div key={e.id} className="group relative">
                              <button
                                onClick={() => toggleStatus(e.id)}
                                className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors flex items-center gap-1 pr-5 ${
                                  filters.status?.includes(e.id)
                                    ? "border-orange-400 bg-orange-50 text-orange-700"
                                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                                }`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: e.color || "#6B7280" }} />
                                {e.name}
                              </button>
                              {isProjectEstado && (
                                <button
                                  onClick={ev => handleDeleteEstado(ev, e.id)}
                                  disabled={deletingId === e.id}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all"
                                  title="Eliminar estado"
                                >
                                  <FaTrash className="text-[7px]" />
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {estadosTareas.length === 0 && (
                    <p className="text-[10px] text-gray-400 italic">Sin estados configurados</p>
                  )}
                </div>
              </div>

              {/* Prioridad + Fechas */}
              <div className="flex flex-col gap-4 min-w-[140px]">
                <div>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Prioridad</span>
                  <div className="flex flex-wrap gap-1">
                    {PRIORITIES.map(p => (
                      <button
                        key={p}
                        onClick={() => togglePriority(p)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                          filters.priority?.includes(p)
                            ? "border-orange-400 bg-orange-50 text-orange-700"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 block">Fecha límite</span>
                  <div className="flex flex-col gap-1.5">
                    <input
                      type="date"
                      value={filters.dateFrom || ""}
                      onChange={e => applyFilters({ ...filters, dateFrom: e.target.value })}
                      className="text-[10px] border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-300 w-full"
                    />
                    <span className="text-[10px] text-gray-400 text-center">hasta</span>
                    <input
                      type="date"
                      value={filters.dateTo || ""}
                      onChange={e => applyFilters({ ...filters, dateTo: e.target.value })}
                      className="text-[10px] border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-300 w-full"
                    />
                    {(filters.dateFrom || filters.dateTo) && (
                      <button
                        onClick={() => applyFilters({ ...filters, dateFrom: "", dateTo: "" })}
                        className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-1"
                      >
                        <FaTimes className="text-[9px]" /> Limpiar fechas
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Limpiar todo */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-500 transition-colors ml-auto flex-shrink-0"
        >
          <FaTimes className="text-[10px]" /> Limpiar
        </button>
      )}
    </div>
  )
}

export default FilterBar
